import type { BillingSummary } from "@/schemas/billing";

/**
 * El aviso previo al corte por mora (trd-client#269, sobre trd-octopus#333).
 *
 * Código puro: qué avisar y con qué palabras se decide acá, sin montar la
 * banda ni levantar un backend.
 */

export type AvisoDeMora = {
  /**
   * `true` si lo que viene después es el corte. Si el servidor dice otra cosa
   * —o nada—, el aviso **no** dice "se suspende tu cuenta": sería afirmar un
   * escalón que no sabemos cuál es.
   */
  loQueSigueEsElCorte: boolean;
  /** Días hasta el corte, tal como los manda el servidor. `null` si no vino. */
  dias: number | null;
  /** Cuándo cae el corte (ISO). `null` si no vino. */
  fecha: string | null;
};

/**
 * El aviso, o `null` si no corresponde avisar.
 *
 * # Sólo en `overdue`
 *
 * Es el único escalón en el que avisar sirve: el negocio debe y todavía opera.
 * Esta función sólo redacta la cuenta regresiva. Cancelación, revisión y
 * suspensión se muestran por separado en el layout. El bloqueo refleja el
 * claim del token según el laudo trd-bff#303, no `dunning.step`.
 *
 * # Y sólo si `suspended` no dice lo contrario
 *
 * Con `suspended: true` billing ya revocó la capacidad. Decir "en 5 días se
 * suspende tu cuenta" sería falso — ya está suspendida—. Cuando los dos no
 * coinciden, el aviso se calla en vez de elegir a quién creerle.
 *
 * # Ausente es "no sé"
 *
 * Sin `dunning` —un octopus anterior a #333— no hay aviso. No se deduce nada
 * del `status` ni del `open_bill`: el plazo lo decide la política del plan, y
 * esa política el cliente no la tiene.
 */
export function avisoDeMora(
  resumen: BillingSummary | null | undefined
): AvisoDeMora | null {
  const mora = resumen?.dunning;
  if (!mora || mora.step !== "overdue") return null;
  if (resumen?.suspended === true) return null;

  return {
    loQueSigueEsElCorte: mora.next_step === "blocked",
    dias:
      typeof mora.days_to_next_step === "number"
        ? mora.days_to_next_step
        : null,
    fecha: mora.next_step_at ?? null,
  };
}

/**
 * El texto de la banda. Las palabras son las del mail y la notificación que
 * manda octopus (`dunning_announcer.go`): el dueño tiene que leer lo mismo en
 * la app que en su casilla.
 *
 * `cuando` es la fecha del corte **ya formateada**, o `null`.
 *
 * Lo que nunca hace: decir "en 0 días". El servidor redondea hacia arriba, así
 * que mientras está en `overdue` el número mínimo es 1 ("mañana"); si igual
 * llega un 0 o menos —una evaluación justo en el borde—, se dice "hoy".
 */
export function textoDelAviso(
  aviso: AvisoDeMora,
  cuando: string | null
): string {
  if (!aviso.loQueSigueEsElCorte) return "Tenés un pago pendiente.";

  const { dias } = aviso;
  if (dias === null) {
    return cuando
      ? `Tenés un pago pendiente: el ${cuando} se suspende tu cuenta.`
      : "Tenés un pago pendiente. Si no lo pagás, se suspende tu cuenta.";
  }
  if (dias <= 0) return "Tenés un pago pendiente: hoy se suspende tu cuenta.";
  if (dias === 1) {
    return "Tenés un pago pendiente: mañana se suspende tu cuenta.";
  }
  return cuando
    ? `Tenés un pago pendiente: en ${dias} días —el ${cuando}— se suspende tu cuenta.`
    : `Tenés un pago pendiente: en ${dias} días se suspende tu cuenta.`;
}
